import React, { useState, useEffect, useCallback } from 'react';
import { string, object, bool, func, array, shape } from 'prop-types';
import classNames from 'classnames/bind';
import Autosuggest from 'react-autosuggest';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { splitEvery } from 'ramda';

import Hint from 'components/Hint';
import AutosuggestEmpty from 'components/AutosuggestEmpty';
import AutosuggestModelItem from './components/AutosuggestModelItem';

import { withLabels } from 'containers/withLabels';
import { withErrorBoundary } from 'containers/ErrorBoundary';
import { withSearchContext } from 'containers/withSearchContext';
import { withCountryContext } from 'containers/withCountryContext';

import {
  getCarAutosuggestions,
  getCarDetails,
} from 'utils/getCarAutosuggestions';
import CrossIcon from 'media/icons/cross.icon.svg';

import styles from './AutosuggestModel.module.scss';
const cx = classNames.bind(styles);

/**
 * Escape regex characters.
 * https://developer.mozilla.org/en/docs/Web/JavaScript/Guide/Regular_Expressions#Using_Special_Characters
 * @param {*} str
 */
const escapeRegexCharacters = (str) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Get suggestion. Search in fetched data.
 * @param {*} value
 */
const getSuggestions = (value, suggestionData) => {
  const escapedValue = escapeRegexCharacters(value.trim());

  if (escapedValue === '' && value.length) {
    return [];
  }

  const regex = new RegExp('(?:^|\\s)' + escapedValue, 'i');
  const suggestions = suggestionData.filter((item) => {
    return regex.test(getSuggestionValue(item));
  });

  return suggestions;
};

/**
 * Get suggestion value.
 * @param {*} suggestion
 */
const getSuggestionValue = (suggestion) => {
  return `${suggestion.modelname}`;
};

/**
 * Render suggestions.
 * @param {*} suggestion
 * @param {*} param1
 */
const renderSuggestion = (suggestion, { query }) => {
  const suggestionText = getSuggestionValue(suggestion);
  const matches = match(suggestionText, query);
  const manufactures = parse(suggestionText, matches);

  return (
    <AutosuggestModelItem manufactures={manufactures} suggestion={suggestion} />
  );
};

const AutosuggestModel = ({
  className,
  type,
  pageType,
  id,
  modelInputValue,
  placeholder,
  setCarInputValue,
  updatePartValue,
  setModelInputValue,
  setDataCarTypes,
  isCarPlateSearch,
  dataModels,
  labels,
  contextSearch: {
    setIsErrorModel,
    setIsErrorCar,
    setIsErrorPart,
    setIsSelectedModel,
    setIsSelectedCar,
    setIsModelTooltip,

    setModelName,
    setModelId,
    setCarName,
    setCarId,
    setPartName,
    setPartId,

    setCarTypeName,
    setIsEditedModelField,
    isEditedModelField,
    isSelectedManu,
    isSelectedCarPlate,
    isSelectedModel,
    vehicleType,

    manuId,
    modelId,
    modelName,
  },
  contextCountry: { currentCountry },
}) => {
  const [isNoSuggestions, setIsNoSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [dataCarTypesLocal, setDataCarTypesLocal] = useState([]);

  const currentValue = isEditedModelField ? modelInputValue : modelName;
  const inputProps = {
    onBlur: () => {
      setIsErrorModel(modelInputValue.length > 0);
    },
    value: currentValue && !isSelectedCarPlate ? currentValue : '',
    className,
    type,
    id,
    placeholder,
    disabled: !isSelectedManu,
    autoFocus: false,
  };

  const resetAllDepsSelect = useCallback(() => {
    updatePartValue('');
    setCarInputValue('');
    setCarTypeName('');
    setIsErrorCar(false);
    setIsSelectedModel(false);
    setIsErrorModel(false);
    setIsSelectedCar(false);

    setCarName('');
    setCarId(null);
    setModelName('');
    setModelId(null);
    setPartName('');
    setPartId(null);
  }, []);

  const onChangeHandle = (event, { newValue }) => {
    setModelInputValue(newValue);
    setIsEditedModelField(true);

    if (modelName) {
      resetAllDepsSelect()
    }
  };

  const renderSuggestionsContainer = ({ containerProps, children, query }) => {
    return (
      <div {...containerProps}>
        {pageType === 'home' && (
          <Hint className={cx('hint')} arrow={true}>
            {labels.tipModelSelect}
          </Hint>
        )}
        <div className="react-autosuggest__suggestions-container-wrapper">
          {children}
        </div>
      </div>
    );
  };

  const onSuggestionSelected = (e, { suggestion: { modelname, modelId } }) => {
    setModelName(modelname);
    setModelId(modelId);

    setDataCarTypesLocal([]);
    getCarsData(manuId, modelId, true);
  };

  const getCarsData = (manuId, modelId, isUpdateStates = false) => {
    getCarAutosuggestions(
      {
        manuId: manuId,
        modId: modelId,
      },
      currentCountry,
      vehicleType
    ).then((response) => {
      getCarAutosuggestionsSuccess(response, isUpdateStates);
    });
  };

  const getCarAutosuggestionsSuccess = (
    { status, data: response },
    isUpdateStates
  ) => {
    if (status === 200 && response.status === 200) {
      if (response.data && response.data.array) {
        const cars = response.data.array.map((element) => {
          return element.carId;
        });
        if (cars.length) {
          splitEvery(25, cars).forEach((group) => {
            getCarDetails(group, currentCountry, vehicleType).then(
              (responseDetails) => {
                getCarDetailsSuccess(responseDetails, isUpdateStates);
              }
            );
          });
        }
      }
    }
  };

  const getCarDetailsSuccess = ({ status, data: response }, isUpdateStates) => {
    const partField = document.getElementById('search-car-type');
    if (status === 200 && response.status === 200) {
      setDataCarTypes(dataCarTypesLocal.concat(response.data.array));

      if (isUpdateStates) {
        setIsErrorPart(false);
        setIsErrorModel(false);
        setIsSelectedModel(true);

        if (partField && window.innerWidth > 768) {
          partField.focus();
        }
      }
    }
  };

  const onSuggestionsFetchRequested = ({ value }) => {
    const dataSuggestions = getSuggestions(value, dataModels);

    setSuggestions(dataSuggestions);
    setIsNoSuggestions(dataSuggestions.length === 0);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
    setIsNoSuggestions(false);
  };

  const onFocusHandle = () => {
    setIsModelTooltip(true);
    setIsErrorModel(false);
  };

  useEffect(() => {
    setIsEditedModelField(false);
    if (manuId && modelId && pageType !== 'home') {
      setIsSelectedModel(true);
      getCarsData(manuId, modelId);
    }
  }, []);

  return (
    <div
      className={cx('root', pageType, {
        'is-car-plate-search': isCarPlateSearch,
      })}
    >
      {isSelectedModel && !isSelectedCarPlate && modelName && (
        <button
          className={cx('cross-btn')}
          type="button"
          onClick={() => {
            setModelInputValue('');
            resetAllDepsSelect();
          }}
        >
          <CrossIcon />
        </button>
      )}
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestionsContainer={renderSuggestionsContainer}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={() => true}
        renderSuggestion={renderSuggestion}
        inputProps={{
          ...inputProps,
          onFocus: onFocusHandle,
          onChange: onChangeHandle,
        }}
        id={id}
      />
      {isNoSuggestions && <AutosuggestEmpty searchValue={modelInputValue} />}
    </div>
  );
};

AutosuggestModel.propTypes = {
  className: string,
  focusOnBind: bool,
  dataModels: array.isRequired,
  type: string.isRequired,
  pageType: string.isRequired,
  labels: object.isRequired,
  contextSearch: object.isRequired,
  id: string.isRequired,
  isCarPlateSearch: bool.isRequired,
  placeholder: string.isRequired,
  updatePartValue: func.isRequired,
  setDataCarTypes: func.isRequired,
  setModelInputValue: func.isRequired,
  setCarInputValue: func.isRequired,
  modelInputValue: string.isRequired,
  contextCountry: shape({
    currentCountry: string.isRequired,
  }).isRequired,
};

AutosuggestModel.defaultProps = {
  className: null,
  isCarPlateSearch: false,
  focusOnBind: false,
};

export default withLabels(
  withSearchContext(withCountryContext(withErrorBoundary(AutosuggestModel)))
);



// WEBPACK FOOTER //
// ./src/components/AutosuggestModel/AutosuggestModel.js